import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, tareasService } from '../services/api';
import Pagination from '../components/Pagination';
import ConfirmPopup from '../components/ConfirmPopup';
import { CALIFICACIONES_PAGE_SIZE } from '../constants/uploadLimits';
import './Calificaciones.css';

const Calificaciones = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [cursosUnicos, setCursosUnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursoFilter, setCursoFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = CALIFICACIONES_PAGE_SIZE;

  const load = useCallback(async (page, cursoId) => {
    setLoading(true);
    setError('');
    try {
      const params = { page, page_size: itemsPerPage };
      if (cursoId) params.curso_id = cursoId;
      const { results, total, cursos } = await tareasService.getCalificaciones(params);
      setList(Array.isArray(results) ? results : []);
      setTotal(typeof total === 'number' ? total : 0);
      setCursosUnicos(Array.isArray(cursos) ? cursos : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar calificaciones.');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    load(currentPage, cursoFilter || undefined);
  }, [navigate, currentPage, cursoFilter, load]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);
  const currentPageActual = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(() => {
    if (currentPage !== currentPageActual) {
      setCurrentPage(currentPageActual);
    }
  }, [currentPage, currentPageActual]);

  const handleCursoFilterChange = (value) => {
    setCursoFilter(value);
    setCurrentPage(1);
  };

  const rangoDesde = total === 0 ? 0 : (currentPageActual - 1) * itemsPerPage + 1;
  const rangoHasta = total === 0 ? 0 : Math.min(currentPageActual * itemsPerPage, total);

  if (loading && list.length === 0) {
    return (
      <div className="calificaciones-container">
        <div className="calificaciones-loading">
          <div className="loading-spinner" />
          <p>Cargando calificaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calificaciones-container">
      <div className="calificaciones-header">
        <h1 className="calificaciones-title">Correcciones</h1>
        <p className="calificaciones-subtitle">Trabajos de tus cursos con su estado y calificación</p>
      </div>
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      {cursosUnicos.length > 0 && (
        <div className="calificaciones-filtro">
          <label htmlFor="calificaciones-curso-filter" className="calificaciones-filtro-label">Filtrar por curso</label>
          <select
            id="calificaciones-curso-filter"
            className="calificaciones-filtro-select"
            value={cursoFilter}
            onChange={(e) => handleCursoFilterChange(e.target.value)}
          >
            <option value="">Todos los cursos</option>
            {cursosUnicos.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>
      )}
      <div className={`calificaciones-table-wrap ${loading && list.length > 0 ? 'pagination-content-loading' : ''}`}>
        <table className="calificaciones-table">
          <thead>
            <tr>
              <th>Trabajo</th>
              <th>Curso</th>
              <th>Calificación</th>
              <th>Estado</th>
              <th>Quién corrigió</th>
              <th className="calificaciones-th-accion">Acción</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={6} className="calificaciones-empty">
                  {total === 0 ? 'No hay trabajos en tus cursos.' : 'No hay trabajos en el curso seleccionado.'}
                </td>
              </tr>
            ) : (
              list.map((row) => {
                const rawLimite = (row.fecha_limite || row.fecha_cierre)
                  ? String(row.fecha_limite || row.fecha_cierre).trim().slice(0, 10)
                  : '';
                const fechaCierre = rawLimite ? (() => { const [y, m, d] = rawLimite.split('-').map(Number); const dt = new Date(y, m - 1, d); dt.setHours(23, 59, 59, 999); return dt; })() : null;
                const hoy = new Date();
                const vencido = row.estado === 'No entregado' && fechaCierre && hoy > fechaCierre;
                // fecha_limite ya incluye una habilitación individual vigente.
                // puede_entregar es true también cuando no hay entrega, aunque el plazo haya vencido.
                const puedeEntregarVencido = vencido && row.aceptar_vencidos === true;
                const esReentrega = row.estado === 'Reentrega' || row.pedir_reentrega === true;
                const puedeEntregar =
                  row.puede_entregar === true ||
                  row.estado === 'No entregado' ||
                  esReentrega;
                const esModificarEntrega = Boolean(
                  puedeEntregar
                  && !esReentrega
                  && row.entrega_id
                  && row.estado !== 'No entregado'
                  && row.estado !== 'Pendiente'
                );
                const fechaLimiteEspecial = row.nueva_fecha_cierre
                  ? (() => {
                      const s = String(row.nueva_fecha_cierre).trim().slice(0, 10);
                      const [y, m, d] = s.split('-').map(Number);
                      const dt = new Date(y, m - 1, d);
                      dt.setHours(23, 59, 59, 999);
                      return dt;
                    })()
                  : null;
                const reentregaVencida = Boolean(
                  (esReentrega || esModificarEntrega)
                  && fechaLimiteEspecial
                  && hoy > fechaLimiteEspecial
                );
                return (
                <tr key={row.tarea_id}>
                  <td className="calificaciones-cell-trabajo">{row.tarea_titulo}</td>
                  <td>{row.curso_nombre || '—'}</td>
                  <td className="calificaciones-cell-nota">
                    {row.nota != null ? (
                      <span className="calificaciones-nota">{String(row.nota)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {row.estado === 'No entregado' ? (
                      <span className={`calificaciones-estado calificaciones-estado--${vencido && !puedeEntregarVencido ? 'no-entregado' : 'pendiente'}`}>
                        {vencido && !puedeEntregarVencido ? 'No entregado' : 'Pendiente'}
                      </span>
                    ) : (
                      <>
                        <span className={`calificaciones-estado calificaciones-estado--${(row.estado || '').toLowerCase().replace(/\s+/g, '-')}`}>
                          {row.estado || '—'}
                        </span>
                        {row.entregado_fuera_de_plazo && (
                          <span className="calificaciones-fuera-de-plazo" title="La entrega se realizó después de la fecha de cierre">
                            Entregado fuera de tiempo
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td>{row.corregido_por_nombre || '—'}</td>
                  <td className="calificaciones-cell-accion">
                    {puedeEntregar && !reentregaVencida && (row.estado === 'No entregado' ? !vencido || puedeEntregarVencido : true) ? (
                      <button
                        type="button"
                        className="calificaciones-btn-entregar"
                        onClick={() => navigate(`/dashboard/entregar/${row.tarea_id}`, { state: { from: 'calificaciones' } })}
                      >
                        {esReentrega ? 'Reentregar' : esModificarEntrega ? 'Modificar entrega' : 'Entregar'}
                      </button>
                    ) : reentregaVencida ? (
                      <span className="calificaciones-estado calificaciones-estado--no-entregado">Reentrega vencida</span>
                    ) : row.estado === 'No entregado' && vencido && !puedeEntregarVencido ? (
                      <span className="calificaciones-estado calificaciones-estado--no-entregado">Vencido</span>
                    ) : row.entrega_id ? (
                      <button
                        type="button"
                        className="calificaciones-btn-ver"
                        onClick={() => navigate(`/dashboard/ver-correccion/${row.entrega_id}`, { state: { from: 'calificaciones' } })}
                      >
                        Ver corrección
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>
      {total > 0 && (
        <p className="calificaciones-pagination-info">
          Mostrando {rangoDesde}–{rangoHasta} de {total} {total === 1 ? 'trabajo' : 'trabajos'}
        </p>
      )}
      {total > itemsPerPage && (
        <Pagination
          currentPage={currentPageActual}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default Calificaciones;
